import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  login,
  saveToken,
  clearToken,
  isLoggedIn,
  fetchAllArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  ArticlePayload,
  fetchBooksAdmin,
  createBook,
  updateBook,
  deleteBook,
  Book,
  BookPayload,
} from '../../services/adminApi';
import logoCircle from '../../assets/logo-circle.jpeg';
import christReignsLogo from '../../assets/crm-newlogo.png';
import { FaSignOutAlt, FaBookOpen, FaPlus, FaBook } from 'react-icons/fa';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Article {
  _id: string;
  title: string;
  subtitle: string;
  content: string;
  url: string;
  publishedAt: string;
  language: 'en' | 'es';
  imageUrl: string;
}

const EMPTY_FORM: ArticlePayload = {
  title: '',
  subtitle: '',
  content: '',
  url: '',
  publishedAt: new Date().toISOString().split('T')[0],
  language: 'en',
  imageUrl: '',
};

// ─── Login Screen ─────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const token = await login(email, password);
      saveToken(token);
      onLogin();
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginCard}>
        <div style={styles.logoRow}>
          <img src={logoCircle} alt="CRM Logo" style={styles.logoImage} />
          <span style={styles.logoText}>Christ Reigns Admin</span>
        </div>
        <h1 style={styles.loginTitle}>Sign In</h1>
        <p style={styles.loginSub}>Access the article management dashboard</p>
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="admin@christreignslab.com"
            required
            autoFocus
          />
          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
          {error && <p style={styles.errorMsg}>{error}</p>}
          <button style={loading ? styles.btnDisabled : styles.btn} type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <div style={styles.footerLinkWrap}>
          <span style={styles.footerText}>Don't have an account? </span>
          <Link to="/admin/register" style={styles.link}>Register</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Cloudinary upload helper ─────────────────────────────────────────────────
// Uploads a file directly from the browser to Cloudinary (unsigned preset).
// Requires REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET
// to be set in .env. The returned secure_url is saved as article.imageUrl in MongoDB.
async function uploadToCloudinary(file: File): Promise<string> {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  if (!cloudName || !uploadPreset) {
    throw new Error(
      'Missing REACT_APP_CLOUDINARY_CLOUD_NAME or REACT_APP_CLOUDINARY_UPLOAD_PRESET in .env'
    );
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    { method: 'POST', body: formData }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Cloudinary upload failed');
  }

  const data = await res.json();
  return data.secure_url as string;
}

// ─── Rich Text Editor ─────────────────────────────────────────────────────────

const TOOLBAR_BUTTONS = [
  { label: 'H2',    cmd: 'formatBlock',   val: 'h2',         title: 'Heading 2' },
  { label: 'H3',    cmd: 'formatBlock',   val: 'h3',         title: 'Heading 3' },
  { label: 'B',     cmd: 'bold',          val: '',           title: 'Bold',      style: { fontWeight: 800 } },
  { label: 'I',     cmd: 'italic',        val: '',           title: 'Italic',    style: { fontStyle: 'italic' } },
  { label: 'U',     cmd: 'underline',     val: '',           title: 'Underline', style: { textDecoration: 'underline' } },
  { label: '•—',    cmd: 'insertUnorderedList', val: '',     title: 'Bullet list' },
  { label: '1—',    cmd: 'insertOrderedList',   val: '',     title: 'Numbered list' },
  { label: '❝',     cmd: 'formatBlock',   val: 'blockquote', title: 'Blockquote' },
  { label: '¶',     cmd: 'formatBlock',   val: 'p',          title: 'Normal paragraph' },
];

function RichTextEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const editorRef = React.useRef<HTMLDivElement>(null);
  const isInitialized = React.useRef(false);

  // Set initial HTML only once on mount
  React.useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value || '';
      isInitialized.current = true;
    }
  }, []);

  const exec = (cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val || '');
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (url) exec('createLink', url);
  };

  return (
    <div style={styles.rteWrap}>
      {/* Toolbar */}
      <div style={styles.rteToolbar}>
        {TOOLBAR_BUTTONS.map((btn) => (
          <button
            key={btn.label}
            type="button"
            title={btn.title}
            style={{ ...styles.rteBtn, ...(btn.style || {}) }}
            onMouseDown={(e) => {
              e.preventDefault(); // keep focus in editor
              exec(btn.cmd, btn.val);
            }}
          >
            {btn.label}
          </button>
        ))}
        <button
          type="button"
          title="Insert link"
          style={styles.rteBtn}
          onMouseDown={(e) => { e.preventDefault(); handleLink(); }}
        >
          🔗
        </button>
        <button
          type="button"
          title="Remove formatting"
          style={styles.rteBtn}
          onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}
        >
          ✕
        </button>
      </div>

      {/* Editable area — styled to match medium-html-content */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        style={styles.rteBody}
        onInput={() => { if (editorRef.current) onChange(editorRef.current.innerHTML); }}
        data-placeholder="Start writing your article…"
      />
    </div>
  );
}

// ─── Article Form ─────────────────────────────────────────────────────────────

function ArticleForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: ArticlePayload;
  onSave: (p: ArticlePayload) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<ArticlePayload>(initial);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const set = (k: keyof ArticlePayload, v: string) =>
    setForm(f => ({ ...f, [k]: v }));

  // Handles both the file input onChange and drag-drop drop event
  const handleFileSelected = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPG, PNG, WebP, etc.)');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      set('imageUrl', url);
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelected(file);
  };

  // Determines if the current imageUrl is a Cloudinary URL (new article)
  // vs a legacy wp-content/miro path (existing seeded article)
  const isCloudinaryUrl = (url?: string) =>
    !!url && url.includes('cloudinary.com');
  const isLegacyUrl = (url?: string) =>
    !!url && (url.includes('wp-content') || url.includes('miro.medium.com'));

  const currentImage = form.imageUrl;

  return (
    <div style={styles.formCard}>
      <div style={styles.formRow}>
        <div style={{ flex: 1 }}>
          <label style={styles.label}>Title *</label>
          <input style={styles.input} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Article title" required />
        </div>
        <div style={{ width: 120 }}>
          <label style={styles.label}>Language *</label>
          <select style={styles.input} value={form.language} onChange={e => set('language', e.target.value as 'en' | 'es')}>
            <option value="en">🇺🇸 EN</option>
            <option value="es">🇪🇸 ES</option>
          </select>
        </div>
      </div>

      <label style={styles.label}>Subtitle / Excerpt</label>
      <input style={styles.input} value={form.subtitle ?? ''} onChange={e => set('subtitle', e.target.value)} placeholder="Short description shown on the card" />

      <label style={styles.label}>URL *</label>
      <input style={styles.input} value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://christreignslab.com/article-slug" required />

      {/* ── Cover Image ──────────────────────────────────────────────────────── */}
      <label style={styles.label}>Cover Image</label>

      {/* Show existing legacy image (wp-content / miro) as read-only with replace option */}
      {isLegacyUrl(currentImage) && (
        <div style={styles.legacyImageWrap}>
          <img src={currentImage} alt="current cover" style={styles.legacyThumb} />
          <div style={{ flex: 1 }}>
            <p style={styles.legacyNote}>Current image (legacy path)</p>
            <code style={styles.legacyPath}>{currentImage}</code>
            <button
              type="button"
              style={{ ...styles.btnOutline, marginTop: 8, fontSize: 12, padding: '6px 12px' }}
              onClick={() => set('imageUrl', '')}
            >
              Replace with new upload
            </button>
          </div>
        </div>
      )}

      {/* Show Cloudinary preview if already uploaded */}
      {isCloudinaryUrl(currentImage) && (
        <div style={styles.legacyImageWrap}>
          <img src={currentImage} alt="uploaded cover" style={styles.legacyThumb} />
          <div style={{ flex: 1 }}>
            <p style={{ ...styles.legacyNote, color: '#10b981' }}>✓ Uploaded to Cloudinary</p>
            <button
              type="button"
              style={{ ...styles.btnOutline, marginTop: 8, fontSize: 12, padding: '6px 12px' }}
              onClick={() => set('imageUrl', '')}
            >
              Replace image
            </button>
          </div>
        </div>
      )}

      {/* File upload zone — shown when no image is set yet */}
      {!currentImage && (
        <div
          style={{
            ...styles.uploadZone,
            ...(dragOver ? styles.uploadZoneDrag : {}),
            ...(uploading ? { opacity: 0.6, pointerEvents: 'none' } : {}),
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelected(f); }}
          />
          {uploading ? (
            <>
              <span style={styles.uploadIcon}>⏳</span>
              <p style={styles.uploadText}>Uploading to Cloudinary…</p>
            </>
          ) : (
            <>
              <span style={styles.uploadIcon}>🖼️</span>
              <p style={styles.uploadText}>Drag & drop an image here, or <strong>click to browse</strong></p>
              <p style={styles.uploadHint}>JPG, PNG, WebP — max 10 MB</p>
            </>
          )}
        </div>
      )}

      {uploadError && <p style={styles.errorMsg}>{uploadError}</p>}

      <label style={styles.label}>Published Date</label>
      <input style={{ ...styles.input, maxWidth: 200 }} type="date" value={form.publishedAt ?? ''} onChange={e => set('publishedAt', e.target.value)} />

      <label style={styles.label}>Content</label>
      <RichTextEditor
        value={form.content ?? ''}
        onChange={(html) => set('content', html)}
      />

      <div style={styles.btnRow}>
        <button style={styles.btnOutline} type="button" onClick={onCancel}>Cancel</button>
        <button
          style={(saving || uploading) ? styles.btnDisabled : styles.btn}
          type="button"
          onClick={() => onSave(form)}
          disabled={saving || uploading || !form.title || !form.url}
        >
          {saving ? 'Saving…' : 'Save Article'}
        </button>
      </div>
    </div>
  );
}

// ─── Book Form ─────────────────────────────────────────────────────────────

interface BookFormProps {
  initial: BookPayload;
  onSave: (p: BookPayload) => void;
  onCancel: () => void;
  saving: boolean;
}

function BookForm({ initial, onSave, onCancel, saving }: BookFormProps) {
  const [form, setForm] = useState<BookPayload>({
    title: initial.title || '',
    author: initial.author || '',
    imageUrl: initial.imageUrl || '',
    order: typeof initial.order === 'number' ? initial.order : 0,
    isActive: typeof initial.isActive === 'boolean' ? initial.isActive : true,
    buyUrl: initial.buyUrl || '',
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const set = (k: keyof BookPayload, v: any) =>
    setForm(f => ({ ...f, [k]: v }));

  const handleFileSelected = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file (JPG, PNG, WebP, etc.)');
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const url = await uploadToCloudinary(file);
      set('imageUrl', url);
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelected(file);
  };

  const isCloudinaryUrl = (url?: string) => !!url && url.includes('cloudinary.com');

  return (
    <div style={styles.formCard}>
      <div style={styles.formRow}>
        <div style={{ flex: 1 }}>
          <label style={styles.label}>Book Title *</label>
          <input style={styles.input} value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Mere Christianity" required />
        </div>
        <div style={{ width: 120 }}>
          <label style={styles.label}>Order</label>
          <input style={styles.input} type="number" value={form.order} onChange={e => set('order', parseInt(e.target.value) || 0)} required />
        </div>
      </div>

      <label style={styles.label}>Author *</label>
      <input style={styles.input} value={form.author} onChange={e => set('author', e.target.value)} placeholder="e.g. C.S. Lewis" required />

      <label style={styles.label}>Book Link (URL)</label>
      <input style={styles.input} value={form.buyUrl} onChange={e => set('buyUrl', e.target.value)} placeholder="e.g. https://www.amazon.com/dp/..." />

      <div style={{ marginBottom: 16 }}>
        <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
          <span>Show on Homepage (Active)</span>
        </label>
      </div>

      {/* ── Book Cover Image ────────────────────────────────────────────────── */}
      <label style={styles.label}>Book Cover Image</label>
      {form.imageUrl && (
        <div style={styles.legacyImageWrap}>
          <img src={form.imageUrl} alt="book cover" style={styles.legacyThumb} />
          <div style={{ flex: 1 }}>
            <p style={{ ...styles.legacyNote, color: isCloudinaryUrl(form.imageUrl) ? '#10b981' : '#6b7280' }}>
              {isCloudinaryUrl(form.imageUrl) ? '✓ Uploaded to Cloudinary' : 'Current cover image'}
            </p>
            <button
              type="button"
              style={{ ...styles.btnOutline, marginTop: 8, fontSize: 12, padding: '6px 12px' }}
              onClick={() => set('imageUrl', '')}
            >
              Replace image
            </button>
          </div>
        </div>
      )}

      {!form.imageUrl && (
        <div
          style={{
            ...styles.uploadZone,
            ...(dragOver ? styles.uploadZoneDrag : {}),
            ...(uploading ? { opacity: 0.6, pointerEvents: 'none' } : {}),
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelected(f); }}
          />
          {uploading ? (
            <>
              <span style={styles.uploadIcon}>⏳</span>
              <p style={styles.uploadText}>Uploading to Cloudinary…</p>
            </>
          ) : (
            <>
              <span style={styles.uploadIcon}>📚</span>
              <p style={styles.uploadText}>Drag & drop cover image here, or <strong>click to browse</strong></p>
              <p style={styles.uploadHint}>JPG, PNG, WebP — max 10 MB</p>
            </>
          )}
        </div>
      )}

      {uploadError && <p style={styles.errorMsg}>{uploadError}</p>}

      <div style={styles.btnRow}>
        <button style={styles.btnOutline} type="button" onClick={onCancel}>Cancel</button>
        <button
          style={(saving || uploading) ? styles.btnDisabled : styles.btn}
          type="button"
          onClick={() => onSave(form)}
          disabled={saving || uploading || !form.title || !form.author}
        >
          {saving ? 'Saving…' : 'Save Book'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Admin Panel ─────────────────────────────────────────────────────────

const AdminPage: React.FC = () => {
  const [authed, setAuthed] = useState(isLoggedIn());
  const [articles, setArticles] = useState<Article[]>([]);
  const getUserName = () => {
    const token = localStorage.getItem('crm_admin_token');
    if (!token) return 'Alan Jones';
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      if (payload.name) return payload.name;
      if (payload.email) {
        if (payload.email === 'admin@christreignslab.com') return 'Alan Jones';
        const namePart = payload.email.split('@')[0];
        return namePart.charAt(0).toUpperCase() + namePart.slice(1);
      }
      return 'Alan Jones';
    } catch (e) {
      return 'Alan Jones';
    }
  };
  const [loadingList, setLoadingList] = useState(false);
  const [view, setView] = useState<'list' | 'create' | 'edit' | 'books' | 'book_create' | 'book_edit'>('list');
  const [editTarget, setEditTarget] = useState<Article | null>(null);
  const [editBookTarget, setEditBookTarget] = useState<Book | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState<'all' | 'en' | 'es'>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteBookConfirm, setDeleteBookConfirm] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<'list' | 'create' | 'books' | 'book_create' | null>(null);
  const [books, setBooks] = useState<Book[]>([]);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const loadArticles = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await fetchAllArticles();
      const sorted = list.sort((a: any, b: any) =>
        new Date(b.publishedAt || 0).getTime() - new Date(a.publishedAt || 0).getTime()
      );
      setArticles(sorted);
    } catch (e: any) {
      showToast(e.message || 'Failed to load articles', false);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadBooks = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await fetchBooksAdmin();
      const sorted = list.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
      setBooks(sorted);
    } catch (e: any) {
      showToast(e.message || 'Failed to load books', false);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      loadArticles();
      loadBooks();
    }
  }, [authed, loadArticles, loadBooks]);

  const handleCreate = async (payload: ArticlePayload) => {
    setSaving(true);
    try {
      await createArticle(payload);
      showToast('Article created successfully ✓');
      setView('list');
      loadArticles();
    } catch (e: any) {
      showToast(e.message || 'Create failed', false);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload: ArticlePayload) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateArticle(editTarget._id, payload);
      showToast('Article updated ✓');
      setView('list');
      setEditTarget(null);
      loadArticles();
    } catch (e: any) {
      showToast(e.message || 'Update failed', false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteArticle(id);
      showToast('Article deleted');
      setArticles(a => a.filter(x => x._id !== id));
    } catch (e: any) {
      showToast(e.message || 'Delete failed', false);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleCreateBook = async (payload: BookPayload) => {
    setSaving(true);
    try {
      await createBook(payload);
      showToast('Book added successfully ✓');
      setView('books');
      loadBooks();
    } catch (e: any) {
      showToast(e.message || 'Add book failed', false);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateBook = async (payload: BookPayload) => {
    if (!editBookTarget) return;
    setSaving(true);
    try {
      await updateBook(editBookTarget._id, payload);
      showToast('Book updated ✓');
      setView('books');
      setEditBookTarget(null);
      loadBooks();
    } catch (e: any) {
      showToast(e.message || 'Update book failed', false);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBook = async (id: string) => {
    try {
      await deleteBook(id);
      showToast('Book deleted');
      setBooks(b => b.filter(x => x._id !== id));
    } catch (e: any) {
      showToast(e.message || 'Delete book failed', false);
    } finally {
      setDeleteBookConfirm(null);
    }
  };

  const logout = () => {
    clearToken();
    setAuthed(false);
    setArticles([]);
    setBooks([]);
    setView('list');
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  const filtered = articles.filter(a => {
    const q = search.toLowerCase();
    const matchSearch = !q || a.title.toLowerCase().includes(q) || (a.subtitle || '').toLowerCase().includes(q);
    const matchLang = langFilter === 'all' || a.language === langFilter;
    return matchSearch && matchLang;
  });

  return (
    <div style={styles.page}>
      {/* Top Header */}
      <header style={styles.topHeader}>
        <div style={styles.headerLeft}>
          <img src={christReignsLogo} alt="Christ Reigns Media" style={styles.logoImageHeader} />
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcomeText}>Welcome, {getUserName()}</span>
          <button style={styles.logoutBtnHeader} onClick={logout}>
            <FaSignOutAlt style={{ fontSize: 14 }} /> LOGOUT
          </button>
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div style={{ ...styles.toast, background: toast.ok ? '#10b981' : '#ef4444' }}>
          {toast.msg}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Delete this article?</p>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>This cannot be undone.</p>
            <div style={styles.btnRow}>
              <button style={styles.btnOutline} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ ...styles.btn, background: '#ef4444' }} onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete book confirm modal */}
      {deleteBookConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Delete this book?</p>
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>This cannot be undone.</p>
            <div style={styles.btnRow}>
              <button style={styles.btnOutline} onClick={() => setDeleteBookConfirm(null)}>Cancel</button>
              <button style={{ ...styles.btn, background: '#ef4444' }} onClick={() => handleDeleteBook(deleteBookConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <aside style={styles.sidebar}>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 0 }}>
          <button
            style={view === 'list' ? styles.navItemActive : (hoveredBtn === 'list' ? { ...styles.navItem, ...styles.navItemHover } : styles.navItem)}
            onClick={() => { setView('list'); setEditTarget(null); setEditBookTarget(null); }}
            onMouseEnter={() => setHoveredBtn('list')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            <FaBookOpen style={styles.sidebarIcon} /> ARTICLES
          </button>
          <button
            style={(view === 'create' || view === 'edit') ? styles.navItemActive : (hoveredBtn === 'create' ? { ...styles.navItem, ...styles.navItemHover } : styles.navItem)}
            onClick={() => { setView('create'); setEditTarget(null); setEditBookTarget(null); }}
            onMouseEnter={() => setHoveredBtn('create')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            <FaPlus style={styles.sidebarIcon} /> NEW ARTICLE
          </button>

          <div style={{ height: 1, backgroundColor: '#e5e7eb', margin: '8px 0' }} />

          <button
            style={view === 'books' ? styles.navItemActive : (hoveredBtn === 'books' ? { ...styles.navItem, ...styles.navItemHover } : styles.navItem)}
            onClick={() => { setView('books'); setEditTarget(null); setEditBookTarget(null); }}
            onMouseEnter={() => setHoveredBtn('books')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            <FaBook style={styles.sidebarIcon} /> BOOK LIBRARY
          </button>
          <button
            style={(view === 'book_create' || view === 'book_edit') ? styles.navItemActive : (hoveredBtn === 'book_create' ? { ...styles.navItem, ...styles.navItemHover } : styles.navItem)}
            onClick={() => { setView('book_create'); setEditTarget(null); setEditBookTarget(null); }}
            onMouseEnter={() => setHoveredBtn('book_create')}
            onMouseLeave={() => setHoveredBtn(null)}
          >
            <FaPlus style={styles.sidebarIcon} /> NEW BOOK
          </button>
        </nav>
      </aside>

      {/* Main content */}
      <main style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>
              {view === 'list'
                ? 'Articles'
                : view === 'create'
                ? 'New Article'
                : view === 'edit'
                ? 'Edit Article'
                : view === 'books'
                ? 'Book Library'
                : view === 'book_create'
                ? 'New Book'
                : 'Edit Book'}
            </h1>
            <p style={styles.pageSub}>
              {view === 'list'
                ? `${articles.length} total articles in MongoDB`
                : view === 'books'
                ? `${books.length} total books in MongoDB`
                : 'Fill out the form below and click Save'}
            </p>
          </div>
          {view === 'list' && (
            <button style={styles.btn} onClick={() => { setView('create'); setEditTarget(null); }}>
              + New Article
            </button>
          )}
          {view === 'books' && (
            <button style={styles.btn} onClick={() => { setView('book_create'); setEditBookTarget(null); }}>
              + New Book
            </button>
          )}
        </div>

        {/* List view */}
        {view === 'list' && (
          <>
            {/* Filters */}
            <div style={styles.filterRow}>
              <input
                style={{ ...styles.input, maxWidth: 320, marginBottom: 0 }}
                placeholder="Search articles…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div style={styles.langTabs}>
                {(['all', 'en', 'es'] as const).map(l => (
                  <button key={l} style={langFilter === l ? styles.tabActive : styles.tab} onClick={() => setLangFilter(l)}>
                    {l === 'all' ? 'All' : l === 'en' ? '🇺🇸 EN' : '🇪🇸 ES'}
                  </button>
                ))}
              </div>
              <button style={styles.btnOutline} onClick={loadArticles}>↻ Refresh</button>
            </div>

            {/* Table */}
            {loadingList ? (
              <div style={styles.emptyState}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={styles.emptyState}>No articles found.</div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Title</th>
                      <th style={{ ...styles.th, width: 60 }}>Lang</th>
                      <th style={{ ...styles.th, width: 110 }}>Published</th>
                      <th style={{ ...styles.th, width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a, i) => (
                      <tr key={a._id} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={styles.td}>
                          <span style={styles.articleTitle}>{a.title}</span>
                          {a.subtitle && <span style={styles.articleSub}>{a.subtitle.slice(0, 80)}{a.subtitle.length > 80 ? '…' : ''}</span>}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span style={a.language === 'en' ? styles.badgeEn : styles.badgeEs}>{a.language.toUpperCase()}</span>
                        </td>
                        <td style={{ ...styles.td, color: '#6b7280', fontSize: 13 }}>
                          {a.publishedAt ? new Date(a.publishedAt).toLocaleDateString() : '—'}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionBtns}>
                            <button style={styles.editBtn} onClick={() => { setEditTarget(a); setView('edit'); }}>Edit</button>
                            <button style={styles.deleteBtn} onClick={() => setDeleteConfirm(a._id)}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Create view */}
        {view === 'create' && (
          <ArticleForm
            initial={EMPTY_FORM}
            onSave={handleCreate}
            onCancel={() => setView('list')}
            saving={saving}
          />
        )}

        {/* Edit view */}
        {view === 'edit' && editTarget && (
          <ArticleForm
            initial={{
              title: editTarget.title,
              subtitle: editTarget.subtitle,
              content: editTarget.content,
              url: editTarget.url,
              publishedAt: editTarget.publishedAt ? editTarget.publishedAt.split('T')[0] : '',
              language: editTarget.language,
              imageUrl: editTarget.imageUrl,
            }}
            onSave={handleUpdate}
            onCancel={() => { setView('list'); setEditTarget(null); }}
            saving={saving}
          />
        )}

        {/* Books List view */}
        {view === 'books' && (
          <>
            {/* Filters */}
            <div style={styles.filterRow}>
              <div style={{ flex: 1 }} />
              <button style={styles.btnOutline} onClick={loadBooks}>↻ Refresh</button>
            </div>

            {/* Table */}
            {loadingList ? (
              <div style={styles.emptyState}>Loading…</div>
            ) : books.length === 0 ? (
              <div style={styles.emptyState}>No books found.</div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, width: 80 }}>Cover</th>
                      <th style={styles.th}>Title</th>
                      <th style={styles.th}>Author</th>
                      <th style={{ ...styles.th, width: 80 }}>Order</th>
                      <th style={{ ...styles.th, width: 100 }}>Status</th>
                      <th style={{ ...styles.th, width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {books.map((b, i) => (
                      <tr key={b._id} style={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          {b.imageUrl ? (
                            <img src={b.imageUrl} alt={b.title} style={{ width: 40, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                          ) : (
                            <span style={{ fontSize: 20 }}>📚</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.articleTitle}>{b.title}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 500, color: '#374151' }}>{b.author}</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span>{b.order}</span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <span style={b.isActive ? styles.badgeEn : styles.badgeEs}>
                            {b.isActive ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionBtns}>
                            <button style={styles.editBtn} onClick={() => { setEditBookTarget(b); setView('book_edit'); }}>Edit</button>
                            <button style={styles.deleteBtn} onClick={() => setDeleteBookConfirm(b._id)}>Del</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Create Book view */}
        {view === 'book_create' && (
          <BookForm
            initial={{ title: '', author: '', imageUrl: '', order: 0, isActive: true, buyUrl: '' }}
            onSave={handleCreateBook}
            onCancel={() => setView('books')}
            saving={saving}
          />
        )}

        {/* Edit Book view */}
        {view === 'book_edit' && editBookTarget && (
          <BookForm
            initial={{
              title: editBookTarget.title,
              author: editBookTarget.author,
              imageUrl: editBookTarget.imageUrl,
              order: editBookTarget.order,
              isActive: editBookTarget.isActive,
              buyUrl: editBookTarget.buyUrl,
            }}
            onSave={handleUpdateBook}
            onCancel={() => { setView('books'); setEditBookTarget(null); }}
            saving={saving}
          />
        )}
      </main>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { display: 'flex', minHeight: '100vh', background: '#f9fafb', fontFamily: 'Inter, system-ui, sans-serif', paddingTop: 70 },
  topHeader: {
    height: 70,
    background: '#000000',
    borderTop: '4px solid #05a5b7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  logoImageHeader: {
    height: 35,
    width: 'auto',
    display: 'block',
  },
  welcomeText: {
    color: '#e5e7eb',
    fontSize: 14,
    fontWeight: 500,
  },
  logoutBtnHeader: {
    background: 'transparent',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    textTransform: 'uppercase',
    padding: '6px 12px',
    borderRadius: 4,
    transition: 'all 0.2s',
  },
  sidebar: {
    width: 240,
    background: '#0a0e1a',
    borderRight: '1px solid rgba(255, 255, 255, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 16px',
    flexShrink: 0,
  },
  logoRow: { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: { fontSize: 22, color: '#05a5b7' },
  logoImage: { width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' },
  logoText: { color: '#fff', fontWeight: 700, fontSize: 15, letterSpacing: '-0.3px' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.8)',
    padding: '12px 20px',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '1px',
    fontFamily: 'Oswald, Inter, sans-serif',
    marginBottom: 4,
    transition: 'all 0.2s',
  },
  navItemActive: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    textAlign: 'left',
    background: 'linear-gradient(90deg, #1e40af 0%, #05a5b7 100%)',
    border: 'none',
    color: '#ffffff',
    padding: '12px 20px',
    borderRadius: 9999,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '1px',
    fontFamily: 'Oswald, Inter, sans-serif',
    marginBottom: 4,
    boxShadow: '0 4px 12px rgba(5, 165, 183, 0.2)',
  },
  navItemHover: {
    background: 'rgba(255, 255, 255, 0.08)',
    color: '#ffffff',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  sidebarIcon: {
    marginRight: 12,
    fontSize: 14,
  },
  main: { flex: 1, padding: '32px 40px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  pageTitle: { fontSize: 26, fontWeight: 700, color: '#111827', margin: 0 },
  pageSub: { color: '#6b7280', fontSize: 14, marginTop: 4, margin: '4px 0 0' },
  filterRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  langTabs: { display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 4, gap: 4 },
  tab: { background: 'transparent', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#6b7280' },
  tabActive: { background: '#fff', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#111827', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  tableWrap: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '12px 16px', verticalAlign: 'top', borderBottom: '1px solid #f3f4f6' },
  rowEven: { background: '#fff' },
  rowOdd: { background: '#fafafa' },
  articleTitle: { display: 'block', fontWeight: 600, color: '#111827', fontSize: 14, lineHeight: 1.4 },
  articleSub: { display: 'block', color: '#9ca3af', fontSize: 12, marginTop: 3 },
  badgeEn: { background: '#dbeafe', color: '#1d4ed8', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 },
  badgeEs: { background: '#fef3c7', color: '#b45309', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 },
  actionBtns: { display: 'flex', gap: 6 },
  editBtn: { background: '#f3f4f6', border: 'none', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' },
  deleteBtn: { background: '#fee2e2', border: 'none', padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#dc2626' },
  emptyState: { textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 15 },
  // Upload zone
  uploadZone: { border: '2px dashed #d1d5db', borderRadius: 10, padding: '36px 24px', textAlign: 'center' as const, cursor: 'pointer', marginTop: 4, transition: 'border-color 0.2s, background 0.2s', background: '#fafafa' },
  uploadZoneDrag: { borderColor: '#05a5b7', background: '#f0fdff' },
  uploadIcon: { fontSize: 32, display: 'block', marginBottom: 8 },
  uploadText: { color: '#374151', fontSize: 14, margin: '0 0 4px' },
  uploadHint: { color: '#9ca3af', fontSize: 12, margin: 0 },
  legacyImageWrap: { display: 'flex', gap: 16, alignItems: 'flex-start', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginTop: 4 },
  legacyThumb: { width: 100, height: 70, objectFit: 'cover' as const, borderRadius: 6, flexShrink: 0 },
  legacyNote: { fontSize: 12, fontWeight: 600, color: '#6b7280', margin: '0 0 4px' },
  legacyPath: { fontSize: 11, color: '#9ca3af', wordBreak: 'break-all' as const, display: 'block' },
  // Form
  formCard: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 32, maxWidth: 900 },
  formRow: { display: 'flex', gap: 16, marginBottom: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: 0 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 16 },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box', background: '#fff', marginBottom: 0 },
  btn: { background: '#05a5b7', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnDisabled: { background: '#9ca3af', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'not-allowed', fontWeight: 600, fontSize: 14 },
  btnOutline: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnRow: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 },
  // Login
  loginWrap: { minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, system-ui, sans-serif' },
  loginCard: { background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', padding: '40px 36px', width: 380, boxShadow: '0 4px 24px rgba(0,0,0,0.07)' },
  loginTitle: { fontSize: 24, fontWeight: 700, color: '#111827', margin: '20px 0 6px' },
  loginSub: { color: '#6b7280', fontSize: 14, marginBottom: 28, marginTop: 0 },
  errorMsg: { color: '#dc2626', fontSize: 13, marginTop: 4, marginBottom: 0 },
  footerLinkWrap: { marginTop: 20, textAlign: 'center', fontSize: 13 },
  footerText: { color: '#6b7280' },
  link: { color: '#05a5b7', fontWeight: 600, textDecoration: 'none' },
  // Toast
  toast: { position: 'fixed', top: 20, right: 24, color: '#fff', padding: '12px 20px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
  // Modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 },
  modalCard: { background: '#fff', borderRadius: 12, padding: '28px 32px', minWidth: 320, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' },
  // Rich text editor
  rteWrap: { border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden', marginTop: 4 },
  rteToolbar: { display: 'flex', flexWrap: 'wrap' as const, gap: 2, padding: '6px 8px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  rteBtn: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5, padding: '4px 9px', cursor: 'pointer', fontSize: 13, color: '#374151', minWidth: 30 },
  rteBody: { minHeight: 260, padding: '16px 18px', fontSize: 15, lineHeight: 1.8, color: '#111827', outline: 'none', overflowY: 'auto' as const, background: '#fff' },
};

export default AdminPage;
